import React, { useCallback, useEffect } from 'react'
import {
  Typography,
  // ToggleButton,
  // ToggleButtonGroup,
  Grid,
  Box,
  useTheme,
  IconButton,
} from '@mui/material'

import {
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Legend,
  Bar,
} from 'recharts'
import useCustomStyles from '../../../../../utils/hooks/CustomStylesHook'
import { ChevronLeft, ChevronRight } from '@mui/icons-material'
import dayjs from 'dayjs'
import { GRAPH_PERIODS, DATE_DIRECTIONS, VITAL_KEYS } from '../Constant'
import { useAppSelector } from '../../../../../app/hooks'
import { selectPatientData } from '../../../OpdPatientSlice'
import { getVitalTracking } from '../../../../../services/DeviceServices'

const styles = (theme: any) => ({
  header: {
    textAlign: 'center',
    marginBottom: theme.spacing(3),
  },
  toggleGroup: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },

  toggleBtn: {
    fontSize: '14px',
    fontWeight: 600,
    minWidth: '60px',
  },
  container: {
    padding: '16px',
    backgroundColor: 'black',
    color: 'white',
    borderRadius: '12px',
    textAlign: 'center',
  },
  slider: {
    marginTop: '16px',
  },
  dateDisplay: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
  },
})

const StressTracking: React.FC = () => {
  const theme = useTheme()
  const classes = useCustomStyles(styles, theme)
  const [period] = React.useState(GRAPH_PERIODS.DAY)
  const [currentDate, setCurrentDate] = React.useState(dayjs())
  const [chartData, setChartData] = React.useState([])
  const [avgData, setAvgData] = React.useState<any>({})
  const patient = useAppSelector(selectPatientData)

  // const handlePeriodChange = (
  //   event: React.MouseEvent<HTMLElement>,
  //   newPeriod: string | null
  // ) => {
  //   if (newPeriod !== null) {
  //     setPeriod(newPeriod)
  //   }
  // }

  const changeDate = (direction: string) => {
    const amount =
      period === GRAPH_PERIODS?.DAY
        ? 1
        : period === GRAPH_PERIODS?.WEEK
        ? 7
        : 30
    setCurrentDate((prev) =>
      prev.add(direction === DATE_DIRECTIONS?.NEXT ? amount : -amount, 'day')
    )
  }

  const getColor = (value: number) => {
    if (value <= 25) return '#00ccff' // Calm
    if (value <= 50) return '#32CD32' // Moderate
    if (value <= 75) return '#FFD700' // Stressed
    return '#FF4500' // Anxiety
  }

  const legendItems = [
    { value: 'Calm', color: '#00ccff', range: '< 25' },
    { value: 'Moderate', color: '#32CD32', range: '25-50' },
    { value: 'Stressed', color: '#FFD700', range: '51-75' },
    { value: 'Anxiety', color: '#FF4500', range: '> 75' },
  ]

  const handleGetStressTracking = useCallback(async () => {
    const response = await getVitalTracking(
      patient?.muhId,
      currentDate.format('YYYY-MM-DD'),
      VITAL_KEYS?.STRESS
    )
    if (response?.success) {
      setChartData(response?.data?.stressReadings)
      setAvgData({
        average: response?.data?.average?.toFixed(0),
        minimum: response?.data?.min?.toFixed(0),
        maximum: response?.data?.max?.toFixed(0),
      })
    }
  }, [patient?.muhId, currentDate])

  useEffect(() => {
    handleGetStressTracking()
  }, [handleGetStressTracking])

  const CustomLegend = () => (
    <Box display='flex' justifyContent='center' marginTop={2}>
      {legendItems?.map((item) => (
        <Box
          key={item?.value}
          display='flex'
          justifyContent={'center'}
          alignItems='center'
          flexWrap={'wrap'}
          margin='0 10px'
        >
          <Box component={'span'} color='black' fontSize={'12px'} mr={1.3}>
            {item?.value}
          </Box>
          <Box
            component={'span'}
            width={10}
            height={10}
            marginRight={1.3}
            sx={{ backgroundColor: item?.color }}
          ></Box>
          <Box component={'span'} color='black' fontSize={'12px'}>
            {item?.range}
          </Box>
        </Box>
      ))}
    </Box>
  )

  return (
    <Box m={3}>
      <Box className={classes?.toggleGroup}>
        {/* <ToggleButtonGroup
          value={period}
          exclusive
          onChange={handlePeriodChange}
          color='primary'
          size='small'
        >
          <ToggleButton value='day' className={classes?.toggleBtn}>
            D
          </ToggleButton>
          <ToggleButton value='week' className={classes?.toggleBtn}>
            W
          </ToggleButton>
          <ToggleButton value='month' className={classes?.toggleBtn}>
            M
          </ToggleButton>
        </ToggleButtonGroup> */}
        <Box className={classes?.dateDisplay}>
          <IconButton
            onClick={() => changeDate(DATE_DIRECTIONS?.PREVIOUS)}
            color='inherit'
          >
            <ChevronLeft />
          </IconButton>
          <Typography>
            {currentDate.format(
              period === GRAPH_PERIODS?.DAY
                ? 'MMM DD, YYYY'
                : period === GRAPH_PERIODS?.WEEK
                ? '[Week of] MMM DD, YYYY'
                : 'MMM YYYY'
            )}
          </Typography>
          <IconButton
            onClick={() => changeDate(DATE_DIRECTIONS?.NEXT)}
            color='inherit'
            disabled={
              currentDate.isSame(dayjs(), 'day') ||
              currentDate.isAfter(dayjs(), 'day')
            }
          >
            <ChevronRight />
          </IconButton>
        </Box>
      </Box>

      <Grid container spacing={2} direction={'column'}>
        <Typography fontSize={'16px'} ml={3.5}>
          {/* Stress */}
        </Typography>
        <ResponsiveContainer width='100%' height={300}>
          <BarChart data={chartData}>
            <XAxis dataKey='time' fontSize={'12px'} />
            <YAxis fontSize='12px' />
            <Tooltip />
            <Legend content={<CustomLegend />} />
            <Bar
              barSize={8}
              dataKey='value'
              shape={(props: any) => (
                <rect
                  x={props?.x}
                  y={props?.y}
                  width={props?.width}
                  height={props?.height}
                  fill={getColor(props?.payload?.value)}
                />
              )}
            />
          </BarChart>
        </ResponsiveContainer>

        <Box
          display={'flex'}
          justifyContent={'center'}
          alignItems={'center'}
          gap={3}
          mt={1.5}
          ml={4}
        >
          <Grid
            item
            xs={6}
            textAlign={'center'}
            spacing={2}
            border={'1px solid grey'}
            borderRadius={'20px'}
            p={2}
          >
            <Typography variant='h6'>{avgData?.average}</Typography>
            <Typography variant='subtitle2'>Avg</Typography>
          </Grid>
          <Grid
            item
            xs={6}
            textAlign={'center'}
            spacing={2}
            border={'1px solid grey'}
            borderRadius={'20px'}
            p={2}
          >
            <Typography variant='h6'>{avgData?.minimum}</Typography>
            <Typography variant='subtitle2'>Minimum</Typography>
          </Grid>
          <Grid
            item
            xs={6}
            textAlign={'center'}
            spacing={2}
            border={'1px solid grey'}
            borderRadius={'20px'}
            p={2}
          >
            <Typography variant='h6'>{avgData?.maximum}</Typography>
            <Typography variant='subtitle2'>Maximum</Typography>
          </Grid>
        </Box>
      </Grid>
    </Box>
  )
}

export default StressTracking
